import React, { useMemo } from 'react';
import { ProductCodeKey } from '@dayetopia/types';
import { useLocation } from '@reach/router';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { getFlattenedConfiguration } from '@components/shared/utilProductOptions';
import { PAD_STACK_COUNTS, PROVIOTICS_TABLETS_COUNT } from '@constants';
import { PadSizeType, ProductConfiguration, VariantItem } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import { replaceText } from '@utils/handleTextReplacement';
import { getFromLocalStorage } from '@utils/localStorage';
import { getPadVariantKeys, isPadsStackBox } from '@utils/pads';
import { getMonthsFromPlanCode } from './utils/subscriptions';

interface Props {
  variants: any;
  selected?: boolean;
  hasBorder?: boolean;
  planCode?: string;
}

export default function TamponBoxConfig({ variants, selected, hasBorder = true, planCode }: Props) {
  const { t } = useTranslation();
  const location = useLocation();
  const { country } = useCountry();
  const configurations: ProductConfiguration[] = useMemo(() => (getFromLocalStorage('configurations', true) as any) ?? [], []);

  if (!variants) return null;

  const isStackBox = isPadsStackBox(country);
  const padVariantKeys = getPadVariantKeys(country);
  const hasPads = Object.keys(padVariantKeys).some((padSizeType) => (variants[padVariantKeys[padSizeType as PadSizeType]] || 0) > 0);
  const hasProviotics = Object.keys(variants).some((variant) => variant.includes(ProductCodeKey.Proviotics));

  // Process special variant types like tampon_box_retail_27cr using configurations
  const flattenedVariants: Record<string, number> = { ...variants };

  // Check if we have a special variant format (e.g., tampon_box_retail_27cr)
  const specialVariantKey = Object.keys(variants).find((key) => key.match(/tampon_box_retail_\d+([a-z]+)$/));

  if (specialVariantKey && configurations.length > 0) {
    const config = configurations.find((c) => c.variant === specialVariantKey);
    if (config) {
      const flattenedItems = getFlattenedConfiguration(config, configurations);

      // Convert flattened items to a simple variant:count object
      flattenedItems.forEach((item: VariantItem) => {
        flattenedVariants[item.variant] = item.count;
      });
    }
  }

  const items = [
    {
      count: flattenedVariants.cbdsuper || 0,
      name: `CBD <span>${t('components.super')}</span>`,
      color: Colors.paleGold,
      textColor: Colors.navyBlue
    },
    {
      count: flattenedVariants.cbdregular || 0,
      name: `CBD <span>${t('components.regular')}</span>`,
      color: `${Colors.paleGold50}`,
      textColor: Colors.navyBlue
    },
    {
      count: flattenedVariants.nakedsuper || 0,
      name: `${t('components.naked')} <span>${t('components.super')}</span>`,
      color: Colors.sage,
      textColor: Colors.navyBlue
    },
    {
      count: flattenedVariants.nakedregular || 0,
      name: `${t('components.naked')} <span>${t('components.regular')}</span>`,
      color: `${Colors.sage}50`,
      textColor: Colors.navyBlue
    }
  ];

  if (hasPads) {
    const multiplierPl = isStackBox ? PAD_STACK_COUNTS[PadSizeType.LINER] : 1;
    const multiplierPr = isStackBox ? PAD_STACK_COUNTS[PadSizeType.REGULAR] : 1;
    const multiplierPn = isStackBox ? PAD_STACK_COUNTS[PadSizeType.SUPER] : 1;
    const multiplierPnp = isStackBox ? PAD_STACK_COUNTS[PadSizeType.SUPER_PLUS] : 1;

    items.push({
      count: (flattenedVariants[padVariantKeys.pn] ?? 0) * multiplierPn,
      name: `${t('components.pads')} <span>${t('components.night')}</span>`,
      color: Colors.lilac,
      textColor: Colors.cream
    });

    items.push({
      count: (flattenedVariants[padVariantKeys.pnp] ?? 0) * multiplierPnp,
      name: `${t('components.pads')} <span>${t('components.night-plus')}</span>`,
      color: Colors.navyBlue,
      textColor: Colors.cream
    });

    items.push({
      count: (flattenedVariants[padVariantKeys.pr] ?? 0) * multiplierPr,
      name: `${t('components.pads')} <span>${t('components.regular')}</span>`,
      color: Colors.forestGreen,
      textColor: Colors.cream
    });

    items.push({
      count: (flattenedVariants[padVariantKeys.pl] ?? 0) * multiplierPl,
      name: `${t('components.pads')} <span>${t('components.liners')}</span>`,
      color: Colors.eucalyptus,
      textColor: Colors.cream
    });
  }

  if (hasProviotics) {
    const variantsMultiplier = flattenedVariants[ProductCodeKey.Proviotics] ? flattenedVariants[ProductCodeKey.Proviotics] : 1;
    const planCodeMultiplier = getMonthsFromPlanCode(planCode);
    const multiplier = planCodeMultiplier || variantsMultiplier;

    items.push({
      count: multiplier * PROVIOTICS_TABLETS_COUNT,
      name: `${t('components.proviotics')}`,
      color: Colors.lilac,
      textColor: Colors.cream
    });
  }

  const finalItems = items.filter((item) => item.count > 0);

  return (
    <BoxWrapper $selected={!!selected} $hasBorder={hasBorder}>
      {finalItems.length ? (
        <Inner>
          {finalItems.map((item, index) => (
            <Item key={index}>
              <Count $color={item.color} $textColor={item.textColor}>
                {item.count}
              </Count>
              <Name dangerouslySetInnerHTML={{ __html: replaceText(item.name, country, location) }} />
            </Item>
          ))}
        </Inner>
      ) : null}
    </BoxWrapper>
  );
}

const BoxWrapper = styled.div<{ $selected: boolean; $hasBorder: boolean }>`
  border: ${(props) => (props.$hasBorder ? `1px solid ${Colors.paleGold}` : 'none')};
  background-color: ${(props) => (props.$selected ? Colors.ecru : 'transparent')};

  & > div {
    border-color: ${(props) => (props.$selected ? Colors.paleGold : 'transparent')};
  }

  &:empty {
    display: none;
  }
`;

const Inner = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  padding: 20px 20px 0;
  border: 1px solid transparent;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  padding: 0 10px;
  color: ${Colors.darkGreen};
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 20px;
`;

const Name = styled.div`
  font-family: 'BrownStd';
  font-size: 11px;
  line-height: 15px;
  text-align: center;
  letter-spacing: 1px;
  text-transform: uppercase;
  font-weight: 400;

  span {
    font-weight: 700;
    display: block;
  }
`;

const Count = styled.div<{ $color: string; $textColor: string }>`
  background: ${(props) => props.$color};
  color: ${(props) => props.$textColor};
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: bold;
  font-size: 15px;
  line-height: 28px;
  text-align: center;
  height: 28px;
  width: 28px;
  border-radius: 50%;
  margin-bottom: 8px;
`;
